import {
  Wws_PageItem,
  Wws_Page,
  Wws_HeadlineItem,
  Wws_ParagraphItem,
  Wws_PersonItem,
  Wws_PhotoItem,
  Wws_GifItem,
  Wws_QuestionItem,
  Wws_LivestreamItem,
  Wws_EmbedItem,
  Wws_PhotoTimelineItem,
  Wws_PhotoGalleryItem,
  Wws_DressCodeItem,
  Wws_StoryItem,
  Wws_AccommodationItem,
} from '../types/graphql';
import { ComponentOverrideMap } from './PageContents';
import BusinessItem from './PageItems/BusinessItem';
import ContentItem from './PageItems/ContentItem';
import DressCodeItem, { defaultCopy } from './PageItems/DressCodeItem';
import EmbedItem from './PageItems/EmbedItem';
import EventGroupItem from './PageItems/EventGroupItem';
import GifItem from './PageItems/GifItem';
import HeadlineItem from './PageItems/HeadlineItem';
import LivestreamItem from './PageItems/LivestreamItem';
import ParagraphItem from './PageItems/ParagraphItem';
import PersonItem from './PageItems/PersonItem';
import PhotoGalleryItem from './PageItems/PhotoGalleryItem';
import PhotoItem from './PageItems/PhotoItem';
import PhotoTimelineItem from './PageItems/PhotoTimelineItem';
import QuestionItem from './PageItems/QuestionItem';

const getComponentOverrideForItem = (
  item: Wws_PageItem & { __typename: string },
  componentOverrides: ComponentOverrideMap
) => {
  if (!item?.__typename) {
    return;
  }

  // Return the specific component override
  return componentOverrides.get(item.__typename);
};

interface PageContentProps {
  item: Wws_PageItem;
  componentOverrides: ComponentOverrideMap;
}

export const PageContent = ({ item, componentOverrides }: PageContentProps) => {
  const ComponentOverride = getComponentOverrideForItem(
    item as Wws_PageItem & { __typename: string },
    componentOverrides
  );

  if (ComponentOverride) {
    return (
      <ComponentOverride key={item.id} item={item} className={item.type} />
    );
  }

  const target = item as Wws_PageItem & { __typename: string };

  switch (target?.__typename) {
    case 'WWS_HeadlineItem':
      return (
        <HeadlineItem
          key={item.id}
          item={item as Wws_HeadlineItem}
          className={item.type}
        />
      );

    case 'WWS_ParagraphItem':
      return (
        <ParagraphItem
          key={item.id}
          item={item as Wws_ParagraphItem}
          className={item.type}
        />
      );

    case 'WWS_PersonItem':
      return (
        <PersonItem
          key={item.id}
          item={item as Wws_PersonItem}
          className={item.type}
        />
      );

    case 'WWS_PhotoItem':
      return (
        <PhotoItem
          key={item.id}
          item={item as Wws_PhotoItem}
          className={item.type}
        />
      );
    case 'WWS_GifItem':
      return (
        <GifItem
          key={item.id}
          item={item as Wws_GifItem}
          className={item.type}
        />
      );

    case 'WWS_QuestionItem':
      return (
        <QuestionItem
          key={item.id}
          item={item as Wws_QuestionItem}
          className={item.type}
        />
      );

    case 'WWS_LivestreamItem':
      return (
        <LivestreamItem
          key={item.id}
          item={item as Wws_LivestreamItem}
          className={item.type}
        />
      );

    case 'WWS_EmbedItem':
      return (
        <EmbedItem
          key={item.id}
          item={item as Wws_EmbedItem}
          className={item.type}
        />
      );

    case 'WWS_PhotoTimelineItem':
      return (
        <PhotoTimelineItem
          key={item.id}
          item={item as Wws_PhotoTimelineItem}
          className={item.type}
        />
      );

    case 'WWS_PhotoGalleryItem':
      return (
        <PhotoGalleryItem
          key={item.id}
          item={item as Wws_PhotoGalleryItem}
          className={item.type}
        />
      );

    case 'WWS_TransportationItem':
    case 'WWS_ActivityItem':
    case 'WWS_AccommodationItem':
      return (
        <BusinessItem
          key={item.id}
          item={item as Wws_AccommodationItem}
          className={item.type}
        />
      );

    case 'WWS_BasicItem':
    case 'WWS_StoryItem':
      return (
        <ContentItem
          key={item.id}
          item={item as Wws_StoryItem}
          className={item.type}
        />
      );

    case 'WWS_DressCodeItem':
      return (
        <DressCodeItem
          key={item.id}
          item={item as Wws_DressCodeItem}
          className={item.type}
          copy={defaultCopy}
        />
      );

    default:
      return null;
  }
};

interface EventGroupItemPageContentProps {
  page: Wws_Page;
  item: Wws_PageItem;
  componentOverrides: ComponentOverrideMap;
}

export const EventGroupItemPageContent = ({
  page,
  item,
  componentOverrides,
}: EventGroupItemPageContentProps) => {
  const ComponentOverride = getComponentOverrideForItem(
    item as Wws_PageItem & { __typename: string },
    componentOverrides
  );

  if (ComponentOverride) {
    return <ComponentOverride item={item} />;
  }

  return <EventGroupItem page={page} item={item} />;
};
